import { z } from 'zod/v3';

export const radarRevisionSchema = z.object({
  release: z.string(),
  ring: z.string(),
  body: z.string().optional(),
});

export const radarFlagSchema = z.enum(['new', 'changed', 'default']);

export const radarItemSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    info: z.string().optional(),
    body: z.string(),
    featured: z.boolean(),
    ring: z.string(),
    quadrant: z.string(),
    flag: radarFlagSchema,
    tags: z.array(z.string()).optional(),
    techCapability: z.string().optional(),
    release: z.string(),
    revisions: z.array(radarRevisionSchema).optional(),
    position: z.tuple([z.number(), z.number()]).optional(),
  })
  .passthrough();

export const radarItemListSchema = z.array(radarItemSchema);

export const radarDataSchema = z.object({
  releases: z.array(z.string()),
  tags: z.array(z.string()),
  items: radarItemListSchema,
});

export const radarQuadrantSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  color: z.string(),
  position: z.number().optional(),
});

export const radarRingSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  color: z.string(),
  radius: z.number().optional(),
  strokeWidth: z.number().optional(),
});

export const radarFlagDetailSchema = z.object({
  color: z.string().optional(),
  title: z.string().optional(),
  titleShort: z.string().optional(),
  description: z.string().optional(),
});

export const radarSocialSchema = z.object({
  href: z.string(),
  icon: z.string(),
});

export const radarConfigTogglesSchema = z.object({
  showSearch: z.boolean().optional(),
  showChart: z.boolean().optional(),
  showTagFilter: z.boolean().optional(),
  showQuadrantList: z.boolean().optional(),
  showEmptyRings: z.boolean().optional(),
});

export const radarChartSettingsSchema = z.object({
  size: z.number().optional(),
  blipSize: z.number().optional(),
});

export const radarFuzzySearchSettingsSchema = z.object({
  threshold: z.number().optional(),
  distance: z.number().optional(),
  ignoreLocation: z.boolean().optional(),
  includeScore: z.boolean().optional(),
});

export const radarTechCapabilitySchema = z.object({
  id: z.string(),
  name: z.string(),
  shape: z.enum(['circle', 'square', 'triangle', 'diamond', 'star']),
  color: z.string().optional(),
});

export const radarConfigSchema = z
  .object({
    basePath: z.string(),
    logoFile: z.string().optional(),
    editUrl: z.string().optional(),
    sourceUrl: z.string().optional(),
    baseUrl: z.string().optional(),
    jsFile: z.string().optional(),
    toggles: radarConfigTogglesSchema.optional(),
    sections: z.array(z.string()).optional(),
    colors: z.record(z.string()).optional(),
    quadrants: z.array(radarQuadrantSchema),
    rings: z.array(radarRingSchema),
    techCapabilities: z.array(radarTechCapabilitySchema).optional(),
    flags: z.record(radarFlagDetailSchema).optional(),
    chart: radarChartSettingsSchema.optional(),
    social: z.array(radarSocialSchema).optional(),
    imprint: z.string().optional(),
    labels: z.record(z.string()).optional(),
    fuzzySearch: radarFuzzySearchSettingsSchema.optional(),
  })
  .passthrough();
export const radarSourceSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    type: z.enum(['internal', 'external']).optional(),
    url: z.string().optional(),
  })
  .passthrough();

export const radarSourcesSchema = z.array(radarSourceSchema);

export type RadarRevision = z.infer<typeof radarRevisionSchema>;
export type RadarItem = z.infer<typeof radarItemSchema>;
export type RadarData = z.infer<typeof radarDataSchema>;
export type RadarQuadrant = z.infer<typeof radarQuadrantSchema>;
export type RadarRing = z.infer<typeof radarRingSchema>;
export type RadarFlag = z.infer<typeof radarFlagDetailSchema>;
export type RadarSocial = z.infer<typeof radarSocialSchema>;
export type RadarConfigToggles = z.infer<typeof radarConfigTogglesSchema>;
export type RadarChartSettings = z.infer<typeof radarChartSettingsSchema>;
export type RadarFuzzySearchSettings = z.infer<typeof radarFuzzySearchSettingsSchema>;
export type RadarConfig = z.infer<typeof radarConfigSchema>;
export type RadarSource = z.infer<typeof radarSourceSchema>;
export type RadarTechCapability = z.infer<typeof radarTechCapabilitySchema>;
